import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import { format } from "@gamedev-web/gamedev-sdk-core";
import { HubAccessLevel, HubInvitationStatus } from "@gamedev-web/one-gaming-crm";
import { BasicButton, ButtonType, InlineTabs } from "@gaming/xds-react-js";

import { HubNotification } from "../../components/HubNotification";
import { PartnerEnrollmentRequired } from "../../components/PartnerEnrollmentRequired";
import { PartnerPageLayout } from "../../components/PartnerPageLayout";
import { PageLoadingSpinner } from "../../components/PageLoadingSpinner";
import {
  partnerAccessDeniedErrors,
  partnerAccessDeniedPath,
  partnerQueryParameters,
  redirectToSignIn,
  xboxOnboardSiteLocationPrefix,
} from "../../constants";
import { useRequireAuth } from "../../constants/urlsClientSide";
import { PartnerAccessLevel, usePartnerRouteGuard } from "../../helpers/usePartnerRouteGuard";
import { useStrings } from "../../localization/localizationHelpers";
import { AdminInvitationConflict } from "../../models/adminPage";
import type {
  AdminContact,
  AdminContactLifecycleAction,
  AdminInvitation,
  AdminInvitationInput,
} from "../../models/adminPage";
import { hasAttemptedPartnerCenterEnrollment } from "../../models/studio";
import { getXboxOnboardData } from "../../redux/selectors/xboxOnboardSelectors";
import { InviteContactModal } from "./components/InviteContactModal";
import { ActiveContactsPanel } from "./components/ActiveContactsPanel";
import { PendingInvitationsPanel } from "./components/PendingInvitationsPanel";
import * as styles from "./AdminPage.module.css";

const adminInvitationsUrl = `${xboxOnboardSiteLocationPrefix}/admin/api/invitations`;
const adminContactsUrl = `${xboxOnboardSiteLocationPrefix}/admin/api/contacts`;
const primarySignatoryUrl = `${xboxOnboardSiteLocationPrefix}/primary-signatory`;

type AdminPageProps = Readonly<{
  readOnly?: boolean;
}>;

type AdminNotification =
  | Readonly<{ kind: "success"; title: string }>
  | Readonly<{
      kind: "error";
      title: string;
      description: string;
      retry:
        | Readonly<{ action: "revoke"; invitationId: string }>
        | Readonly<{ action: "resend"; invitation: AdminInvitation }>
        | Readonly<{ action: "role"; contactId: string; value: HubAccessLevel }>
        | Readonly<{ action: "primarySignatory"; contactId: string }>;
    }>;

type QueuedAdminNotification = AdminNotification & Readonly<{ id: number }>;

const fetchAdminMutation = async (url: string, init: RequestInit): Promise<Response> => {
  const response = await fetch(url, init);
  if (response.status === 401 || response.redirected) {
    redirectToSignIn();
    throw new Error("Authentication required");
  }
  return response;
};

const invitationExists = async (email: string): Promise<boolean> => {
  const response = await fetch(adminInvitationsUrl);
  if (!response.ok) {
    return false;
  }
  const invitations = (await response.json()) as AdminInvitation[];
  return invitations.some(
    invitation =>
      invitation.status === HubInvitationStatus.Pending &&
      invitation.email.trim().toLowerCase() === email
  );
};

const removeFromSet = (values: ReadonlySet<string>, value: string) =>
  new Set([...values].filter(item => item !== value));

export const AdminPage = ({ readOnly = false }: AdminPageProps) => {
  const isPageReady = usePartnerRouteGuard(PartnerAccessLevel.NdaSignedOrAaa);
  const isSignedIn = useRequireAuth();
  const xboxOnboardData = useSelector(getXboxOnboardData);
  const isHubAdmin = xboxOnboardData?.isHubAdmin === true;
  const isAaaHubAdmin = xboxOnboardData?.hubType === "aaa" && isHubAdmin;
  const canChangePrimarySignatory =
    xboxOnboardData?.hubPermissions.canChangePrimarySignatory === true;
  const canInvite =
    isAaaHubAdmin ||
    hasAttemptedPartnerCenterEnrollment(xboxOnboardData?.partnerCenterEnrollmentStatus);
  const needsEnrollment =
    !readOnly &&
    isHubAdmin &&
    xboxOnboardData?.hubType !== "aaa" &&
    !hasAttemptedPartnerCenterEnrollment(xboxOnboardData?.partnerCenterEnrollmentStatus);
  const [unavailableTitle, unavailableDescription] = useStrings(
    "partnerPeopleUnavailableTitle",
    "partnerPeopleUnavailableDescription"
  );
  const history = useHistory();
  const [contacts, setContacts] = useState<AdminContact[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const [updatingContactIds, setUpdatingContactIds] = useState<ReadonlySet<string>>(new Set());
  const [isTransferringPrimarySignatory, setIsTransferringPrimarySignatory] = useState(false);
  const [invitations, setInvitations] = useState<AdminInvitation[] | null>(null);
  const [isInvitationsLoading, setIsInvitationsLoading] = useState(false);
  const [invitationsError, setInvitationsError] = useState(false);
  const [revokingInvitationIds, setRevokingInvitationIds] = useState<ReadonlySet<string>>(
    new Set()
  );
  const [resendingInvitationIds, setResendingInvitationIds] = useState<ReadonlySet<string>>(
    new Set()
  );
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [resendInvitation, setResendInvitation] = useState<AdminInvitation | null>(null);
  const [availableInvitation, setAvailableInvitation] = useState<AdminInvitationInput | null>(null);
  const [selectedMemberTab, setSelectedMemberTab] = useState(0);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [notifications, setNotifications] = useState<QueuedAdminNotification[]>([]);
  const nextNotificationId = useRef(0);
  const [
    adminLabel,
    title,
    description,
    loadingLabel,
    inviteLabel,
    activeMembersTitle,
    pendingMembersTitle,
    memberTabsLabel,
    invitationCanceledFormat,
    cancelInvitationErrorTitle,
    cancelInvitationErrorDescription,
    tryAgainLabel,
    closeNotificationLabel,
    invitationSentFormat,
    invitationResentFormat,
    roleUpdatedFormat,
    roleUpdateErrorTitle,
    roleUpdateErrorDescription,
    invitationSendErrorTitle,
    invitationSendErrorDescription,
    primarySignatoryUpdatedFormat,
    primarySignatoryUpdateErrorTitle,
    primarySignatoryUpdateErrorDescription,
  ] = useStrings(
    "partnerAdminLabel",
    "partnerAdminPageTitle",
    "partnerAdminPageDescription",
    "partnerAdminLoadingLabel",
    "partnerAdminInviteLabel",
    "partnerAdminActiveMembersTitle",
    "partnerAdminPendingMembersTitle",
    "partnerAdminMemberTabsLabel",
    "partnerAdminInvitationCanceledFormat",
    "partnerAdminCancelInvitationErrorTitle",
    "partnerAdminCancelInvitationErrorDescription",
    "partnerAdminTryAgainLabel",
    "partnerAdminNotificationCloseLabel",
    "partnerAdminInvitationSentFormat",
    "partnerAdminInvitationResentFormat",
    "partnerAdminRoleUpdatedFormat",
    "partnerAdminRoleUpdateErrorTitle",
    "partnerAdminRoleUpdateErrorDescription",
    "partnerAdminInviteSendErrorTitle",
    "partnerAdminInviteSendError",
    "partnerAdminPrimarySignatoryUpdatedFormat",
    "partnerAdminPrimarySignatoryUpdateErrorTitle",
    "partnerAdminPrimarySignatoryUpdateErrorDescription"
  );

  const closeInvitationModal = () => {
    setIsInviteOpen(false);
    setResendInvitation(null);
    setAvailableInvitation(null);
  };
  const showNotification = (notification: AdminNotification) => {
    const id = nextNotificationId.current++;
    setNotifications(current => [...current, { ...notification, id }]);
  };
  const refreshAndNotify = (title: string) => {
    setRefreshVersion(current => current + 1);
    showNotification({ kind: "success", title });
  };

  const sendInvitation = async (
    invitation: AdminInvitationInput
  ): Promise<AdminInvitationConflict | null> => {
    let response: Response;
    try {
      response = await fetchAdminMutation(adminInvitationsUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(invitation),
      });
    } catch (error) {
      if (await invitationExists(invitation.email)) {
        refreshAndNotify(format(invitationSentFormat, invitation.email));
        return null;
      }
      throw error;
    }
    if (response.status === 409) {
      const content = (await response.json()) as { error?: unknown };
      if (
        content.error === AdminInvitationConflict.ExistingContact ||
        content.error === AdminInvitationConflict.ExistingInvitation
      ) {
        return content.error;
      }
    }
    if (!response.ok) {
      if (await invitationExists(invitation.email)) {
        refreshAndNotify(format(invitationSentFormat, invitation.email));
        return null;
      }
      throw new Error(`Failed to create invitation: ${response.status}`);
    }
    refreshAndNotify(format(invitationSentFormat, invitation.email));
    return null;
  };

  const updateContactAccess = async (contactId: string, accessLevel: HubAccessLevel) => {
    if (updatingContactIds.has(contactId)) {
      return;
    }

    const contact = contacts?.find(item => item.contactId === contactId);
    const name = contact ? `${contact.firstName} ${contact.lastName}`.trim() || contact.email : "";
    setUpdatingContactIds(current => new Set(current).add(contactId));

    try {
      const response = await fetchAdminMutation(`${adminContactsUrl}/${contactId}/access`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ accessLevel }),
      });
      if (!response.ok) {
        throw new Error(`Failed to update Contact access: ${response.status}`);
      }
      setContacts(
        current =>
          current?.map(contact =>
            contact.contactId === contactId ? { ...contact, hubRole: accessLevel } : contact
          ) ?? null
      );
      showNotification({
        kind: "success",
        title: format(roleUpdatedFormat, name),
      });
    } catch {
      showNotification({
        kind: "error",
        title: roleUpdateErrorTitle,
        description: roleUpdateErrorDescription,
        retry: { action: "role", contactId, value: accessLevel },
      });
    } finally {
      setUpdatingContactIds(current => removeFromSet(current, contactId));
    }
  };

  const revokeInvitation = async (invitationId: string) => {
    if (revokingInvitationIds.has(invitationId)) {
      return;
    }

    setRevokingInvitationIds(current => new Set(current).add(invitationId));
    try {
      const response = await fetchAdminMutation(`${adminInvitationsUrl}/${invitationId}/revoke`, {
        method: "PATCH",
      });
      if (!response.ok && response.status !== 409) {
        throw new Error(`Failed to revoke invitation: ${response.status}`);
      }
      setInvitations(
        current =>
          current?.map(invitation =>
            invitation.invitationId === invitationId
              ? { ...invitation, status: HubInvitationStatus.Revoked }
              : invitation
          ) ?? null
      );
      const invitation = invitations?.find(item => item.invitationId === invitationId);
      const name = invitation
        ? `${invitation.firstName} ${invitation.lastName}`.trim() || invitation.email
        : "";
      showNotification({
        kind: "success",
        title: format(invitationCanceledFormat, name),
      });
    } catch {
      showNotification({
        kind: "error",
        title: cancelInvitationErrorTitle,
        description: cancelInvitationErrorDescription,
        retry: { action: "revoke", invitationId },
      });
    } finally {
      setRevokingInvitationIds(current => removeFromSet(current, invitationId));
    }
  };

  const resendInvitationDetails = async (
    selectedInvitation: AdminInvitation,
    invitation: AdminInvitationInput
  ): Promise<AdminInvitationConflict | null> => {
    setResendingInvitationIds(current => new Set(current).add(selectedInvitation.invitationId));
    try {
      const response = await fetchAdminMutation(
        `${adminInvitationsUrl}/${selectedInvitation.invitationId}/resend`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            firstName: invitation.firstName,
            lastName: invitation.lastName,
            jobTitle: invitation.jobTitle,
            email: invitation.email,
            accessLevel: invitation.accessLevel,
          }),
        }
      );
      if (response.status === 409) {
        const content = (await response.json()) as { error?: unknown };
        if (
          content.error === AdminInvitationConflict.ExistingContact ||
          content.error === AdminInvitationConflict.ExistingInvitation
        ) {
          return content.error;
        }
      }
      if (!response.ok) {
        throw new Error(`Failed to resend invitation: ${response.status}`);
      }
      refreshAndNotify(format(invitationResentFormat, invitation.email));
      return null;
    } finally {
      setResendingInvitationIds(current => removeFromSet(current, selectedInvitation.invitationId));
    }
  };
  const resendSelectedInvitation = async (invitation: AdminInvitationInput) => {
    if (!resendInvitation) {
      throw new Error("No invitation selected for resend");
    }
    return resendInvitationDetails(resendInvitation, invitation);
  };
  const resendInvitationWithoutChanges = async (invitation: AdminInvitation) => {
    try {
      const conflict = await resendInvitationDetails(invitation, invitation);
      if (conflict) {
        throw new Error(conflict);
      }
    } catch {
      showNotification({
        kind: "error",
        title: invitationSendErrorTitle,
        description: invitationSendErrorDescription,
        retry: { action: "resend", invitation },
      });
    }
  };

  const updateContactLifecycle = async (contactId: string, action: AdminContactLifecycleAction) => {
    if (updatingContactIds.has(contactId)) {
      return;
    }

    setUpdatingContactIds(current => new Set(current).add(contactId));
    try {
      const response = await fetchAdminMutation(`${adminContactsUrl}/${contactId}/lifecycle`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action }),
      });
      if (!response.ok) {
        throw new Error(`Failed to update Contact lifecycle: ${response.status}`);
      }
      setContacts(current => current?.filter(contact => contact.contactId !== contactId) ?? null);
    } catch {
      setError(true);
    } finally {
      setUpdatingContactIds(current => removeFromSet(current, contactId));
    }
  };

  const transferPrimarySignatory = async (contactId: string) => {
    if (isTransferringPrimarySignatory) {
      return;
    }

    const contact = contacts?.find(item => item.contactId === contactId);
    const name = contact ? `${contact.firstName} ${contact.lastName}`.trim() || contact.email : "";
    const currentPrimarySignatoryId = contacts?.find(item => item.isPrimarySignatory)?.contactId;
    const affectedContactIds = [contactId, currentPrimarySignatoryId].filter(
      (id): id is string => typeof id === "string"
    );
    setIsTransferringPrimarySignatory(true);
    setUpdatingContactIds(current => {
      const next = new Set(current);
      affectedContactIds.forEach(id => next.add(id));
      return next;
    });
    try {
      const response = await fetchAdminMutation(primarySignatoryUrl, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ contactId }),
      });
      if (!response.ok) {
        throw new Error(`Failed to update primary signatory: ${response.status}`);
      }
      setContacts(
        current =>
          current?.map(contact => ({
            ...contact,
            isPrimarySignatory: contact.contactId === contactId,
          })) ?? null
      );
      showNotification({
        kind: "success",
        title: format(primarySignatoryUpdatedFormat, name),
      });
    } catch {
      showNotification({
        kind: "error",
        title: format(primarySignatoryUpdateErrorTitle, name),
        description: primarySignatoryUpdateErrorDescription,
        retry: { action: "primarySignatory", contactId },
      });
    } finally {
      setIsTransferringPrimarySignatory(false);
      setUpdatingContactIds(
        current => new Set([...current].filter(id => !affectedContactIds.includes(id)))
      );
    }
  };

  useEffect(() => {
    if (!readOnly && isPageReady && !isHubAdmin && !needsEnrollment) {
      history.replace(
        `${partnerAccessDeniedPath}?${partnerQueryParameters.accessDeniedError}=${partnerAccessDeniedErrors.admin}`
      );
    }
  }, [history, isHubAdmin, isPageReady, needsEnrollment, readOnly]);

  useEffect(() => {
    if (!isPageReady || !isSignedIn || needsEnrollment || (!readOnly && !isHubAdmin)) {
      return;
    }

    let active = true;
    const isContactsTab = readOnly || selectedMemberTab === 0;
    if (isContactsTab) {
      setContacts(null);
      setError(false);
      setIsLoading(true);
    } else {
      setInvitations(null);
      setInvitationsError(false);
      setIsInvitationsLoading(true);
    }

    fetch(isContactsTab ? adminContactsUrl : adminInvitationsUrl)
      .then(response => {
        if (response.status === 401 || response.redirected) {
          redirectToSignIn();
          return null;
        }
        if (!response.ok) {
          throw new Error(`Failed to fetch admin members: ${response.status}`);
        }
        return response.json() as Promise<AdminContact[] | AdminInvitation[]>;
      })
      .then(data => {
        if (active && data) {
          if (isContactsTab) {
            setContacts(data as AdminContact[]);
          } else {
            setInvitations(data as AdminInvitation[]);
          }
        }
      })
      .catch(() => {
        if (active) {
          if (isContactsTab) {
            setError(true);
          } else {
            setInvitationsError(true);
          }
        }
      })
      .finally(() => {
        if (active) {
          if (isContactsTab) {
            setIsLoading(false);
          } else {
            setIsInvitationsLoading(false);
          }
        }
      });

    return () => {
      active = false;
    };
  }, [
    isHubAdmin,
    isPageReady,
    isSignedIn,
    needsEnrollment,
    readOnly,
    refreshVersion,
    selectedMemberTab,
  ]);

  if (!readOnly && isPageReady && !isHubAdmin && !needsEnrollment) {
    return null;
  }

  if (!isPageReady || !isSignedIn) {
    return (
      <PartnerPageLayout breadcrumbLabel={readOnly ? activeMembersTitle : adminLabel} wide>
        <div role="status" aria-live="polite" aria-label={loadingLabel} aria-busy="true">
          <PageLoadingSpinner />
        </div>
      </PartnerPageLayout>
    );
  }

  if (needsEnrollment) {
    return (
      <PartnerPageLayout breadcrumbLabel={adminLabel} wide>
        <PartnerEnrollmentRequired title={unavailableTitle} description={unavailableDescription} />
      </PartnerPageLayout>
    );
  }

  return (
    <PartnerPageLayout breadcrumbLabel={readOnly ? activeMembersTitle : adminLabel} wide>
      <div className={styles.page}>
        <div className={styles.heading}>
          <h1 className={styles.title}>{title}</h1>
          {!readOnly && (
            <BasicButton
              as="button"
              buttonType={ButtonType.Brand}
              type="button"
              disabled={!canInvite}
              onClick={() => setIsInviteOpen(true)}
            >
              {inviteLabel}
            </BasicButton>
          )}
        </div>
        <p className={styles.description}>{description}</p>
        {notifications.map(notification => (
          <HubNotification
            key={notification.id}
            actionLabel={notification.kind === "error" ? tryAgainLabel : undefined}
            autoDismissMs={notification.kind === "success" ? 5000 : undefined}
            closeLabel={closeNotificationLabel}
            description={notification.kind === "error" ? notification.description : undefined}
            kind={notification.kind}
            onAction={
              notification.kind === "error"
                ? () => {
                    setNotifications(current =>
                      current.filter(item => item.id !== notification.id)
                    );
                    if (notification.retry.action === "revoke") {
                      revokeInvitation(notification.retry.invitationId);
                    } else if (notification.retry.action === "resend") {
                      resendInvitationWithoutChanges(notification.retry.invitation);
                    } else if (notification.retry.action === "primarySignatory") {
                      transferPrimarySignatory(notification.retry.contactId);
                    } else {
                      updateContactAccess(notification.retry.contactId, notification.retry.value);
                    }
                  }
                : undefined
            }
            onClose={() =>
              setNotifications(current => current.filter(item => item.id !== notification.id))
            }
            title={notification.title}
          />
        ))}
        {!readOnly && (
          <InlineTabs
            tabs={[{ label: activeMembersTitle }, { label: pendingMembersTitle }]}
            value={selectedMemberTab}
            onValueChange={setSelectedMemberTab}
            className={styles.tabs}
            contentContainerClassName={styles.tabList}
            selectionIndicatorClassName={styles.tabSelectionIndicator}
            tabLabelClassName={styles.tab}
            aria-label={memberTabsLabel}
          />
        )}
        <ActiveContactsPanel
          contacts={contacts}
          canChangePrimarySignatory={canChangePrimarySignatory}
          error={error}
          isTransferringPrimarySignatory={isTransferringPrimarySignatory}
          isLoading={isLoading}
          isVisible={readOnly || selectedMemberTab === 0}
          readOnly={readOnly}
          showPrimarySignatorySection
          canInvite={canInvite}
          onInvite={contact =>
            setAvailableInvitation({
              firstName: contact.firstName,
              lastName: contact.lastName,
              jobTitle: contact.jobTitle,
              email: contact.email,
              accessLevel: HubAccessLevel.User,
            })
          }
          onRefresh={() => setRefreshVersion(current => current + 1)}
          onRemoveFromCompany={contactId => updateContactLifecycle(contactId, "removeFromCompany")}
          onRemoveHubAccess={contactId => updateContactLifecycle(contactId, "removeHubAccess")}
          onTransferPrimarySignatory={transferPrimarySignatory}
          onUpdateContactAccess={updateContactAccess}
          updatingContactIds={updatingContactIds}
        />
        {!readOnly && (
          <>
            <PendingInvitationsPanel
              error={invitationsError}
              invitations={invitations}
              isLoading={isInvitationsLoading}
              isVisible={selectedMemberTab === 1}
              onEditInvitation={setResendInvitation}
              onRefresh={() => setRefreshVersion(current => current + 1)}
              onRevokeInvitation={revokeInvitation}
              onResendInvitation={resendInvitationWithoutChanges}
              resendingInvitationIds={resendingInvitationIds}
              revokingInvitationIds={revokingInvitationIds}
            />
            <InviteContactModal
              initialInvitation={resendInvitation ?? availableInvitation ?? undefined}
              isEmailReadOnly={availableInvitation !== null}
              isOpen={isInviteOpen || resendInvitation !== null || availableInvitation !== null}
              isResend={resendInvitation !== null}
              onClose={closeInvitationModal}
              onSubmit={resendInvitation ? resendSelectedInvitation : sendInvitation}
            />
          </>
        )}
      </div>
    </PartnerPageLayout>
  );
};

export const MembersPage = () => <AdminPage readOnly />;
